import { useCallback } from 'react'
import { useDialog } from 'hooks/useDialog'
import { Sizes } from 'enums/sizes'
import { DocumentProcessDialog } from './DocumentProcessDialog'
import { DocumentProcessDialogConfig } from './types'
import { filesAPI } from 'services/swagger-api'
import { ContextEnum, FileTypeEnum } from 'api/files'
import { downloadDocumentByStoredId } from 'services/documentsAPI'

export interface UseDocumentProcessDialogOptions<
  TFormValues extends Record<string, unknown>,
  TExtracted = unknown
> {
  config: DocumentProcessDialogConfig<TFormValues, TExtracted>
  context: ContextEnum
  objectId: string
  fileType?: FileTypeEnum
  size?: Sizes
}

export const useDocumentProcessDialog = () => {
  const { openBlankDialog, closeDialog } = useDialog()

  const openDocumentProcess = useCallback(<
    TFormValues extends Record<string, unknown>,
    TExtracted = unknown
  >({
    config,
    context,
    objectId,
    fileType = FileTypeEnum.Rib,
    size = Sizes['5xl']
  }: UseDocumentProcessDialogOptions<TFormValues, TExtracted>) => {

    const handleUploadFile = async (file: File): Promise<{
      storedFileId: string
      blobUrl: string
      mimeType: string
    }> => {
      // Upload the file
      const response = await filesAPI.uploadFile({
        fileType,
        context,
        objetId:objectId,
        file
      })

      if (!response.isSuccess || !response.id) {
        throw new Error(response.errorMessage || 'Erreur lors de l\'upload')
      }

      const storedFileId = response.id

      // Download the file to get blob URL for preview
      const fileData = await downloadDocumentByStoredId(storedFileId)
      const mimeType = file.type || 'application/pdf'
      const blob = new Blob([fileData], { type: mimeType })
      const blobUrl = URL.createObjectURL(blob)

      return { storedFileId, blobUrl, mimeType }
    }

    const handleClose = () => {
      closeDialog()
      config.onCancel?.()
    }

    openBlankDialog(
      { size },
      <DocumentProcessDialog<TFormValues, TExtracted>
        config={config}
        onClose={handleClose}
        onUploadFile={handleUploadFile}
      />
    )
  }, [openBlankDialog, closeDialog])

  return { openDocumentProcess }
}
